import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Input, Modal, Row, Space, Switch, Tag, Typography, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PauseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { loadSuspensionNotice, updateSuspensionNotice } from "../store/app/app.slice";

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * PAP-343: lets a portal supervisor activate the Suspension Notice. While active, the
 * mobile/user app shows the notice when a user taps "New Letter" and the postbox refuses
 * new letters. The switch itself lives in the postbox; this page only relays it.
 */
export default function SuspensionNotice() {
    const dispatch = useDispatch();
    const { suspensionNotice, isSuspensionNoticeLoading, isSuspensionNoticeSaving } = useSelector((state) => state.app);
    const [form] = Form.useForm();
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        dispatch(loadSuspensionNotice({}));
    }, [dispatch]);

    useEffect(() => {
        if (suspensionNotice) {
            form.setFieldsValue({
                messageEn: suspensionNotice.messageEn,
                messageUr: suspensionNotice.messageUr,
            });
            setDirty(false);
        }
    }, [suspensionNotice, form]);

    const enabled = suspensionNotice?.enabled === true;

    const save = (nextEnabled) => {
        const values = form.getFieldsValue();
        dispatch(updateSuspensionNotice({
            finalData: {
                suspensionNotice: {
                    enabled: nextEnabled,
                    messageEn: values.messageEn,
                    messageUr: values.messageUr,
                }
            },
            callbackSuccess: (result) => {
                notification.success({
                    message: result?.enabled ? "Suspension Notice activated" : "Suspension Notice deactivated",
                    description: result?.enabled
                        ? "Users now see the notice when they tap \"New Letter\" and cannot send letters."
                        : "Users can send letters again.",
                });
            },
            callbackError: (message) => {
                notification.error({ message: "Could not update the Suspension Notice", description: message });
            },
        }));
    };

    const onToggle = (checked) => {
        if (checked) {
            Modal.confirm({
                title: "Activate the Suspension Notice?",
                icon: <ExclamationCircleOutlined />,
                content: "All users of the letter app will see the notice instead of the \"New Letter\" flow, and new letters will be refused until you deactivate it.",
                okText: "Activate",
                okButtonProps: { danger: true },
                cancelText: "Cancel",
                onOk: () => save(true),
            });
        } else {
            save(false);
        }
    };

    return (
        <div className="layout-content">
            <Row gutter={[24, 0]}>
                <Col xs={24} xl={16}>
                    <Card
                        bordered={false}
                        className="criclebox"
                        title={
                            <Space>
                                <PauseCircleOutlined />
                                <span>Suspension Notice</span>
                                {enabled
                                    ? <Tag color="red">ACTIVE</Tag>
                                    : <Tag color="green">INACTIVE</Tag>}
                            </Space>
                        }
                        loading={isSuspensionNoticeLoading && !suspensionNotice}
                    >
                        {enabled && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="Letter intake is suspended"
                                description={'Users see this notice when they tap "New Letter". New letters are refused by the postbox.'}
                            />
                        )}

                        <Row align="middle" style={{ marginBottom: 24 }}>
                            <Col flex="none">
                                <Switch
                                    checked={enabled}
                                    loading={isSuspensionNoticeSaving}
                                    onChange={onToggle}
                                    checkedChildren="Active"
                                    unCheckedChildren="Inactive"
                                />
                            </Col>
                            <Col flex="auto" style={{ paddingLeft: 12 }}>
                                <Text>{enabled ? "Deactivate to allow new letters again." : "Activate to suspend new letters in the user app."}</Text>
                            </Col>
                        </Row>

                        <Title level={5}>Notice text</Title>
                        <Form form={form} layout="vertical" onValuesChange={() => setDirty(true)}>
                            <Form.Item name="messageEn" label="English">
                                <TextArea rows={3} maxLength={2000} showCount />
                            </Form.Item>
                            <Form.Item name="messageUr" label="Urdu">
                                <TextArea rows={3} maxLength={2000} showCount dir="rtl" style={{ fontSize: 18, textAlign: "right" }} />
                            </Form.Item>
                            <Space>
                                <Button type="primary" disabled={!dirty} loading={isSuspensionNoticeSaving} onClick={() => save(enabled)}>
                                    Save text
                                </Button>
                                <Button disabled={!dirty} onClick={() => {
                                    form.setFieldsValue({ messageEn: suspensionNotice?.messageEn, messageUr: suspensionNotice?.messageUr });
                                    setDirty(false);
                                }}>
                                    Reset
                                </Button>
                            </Space>
                        </Form>

                        {suspensionNotice?.updatedAt && (
                            <div style={{ marginTop: 24 }}>
                                <Text type="secondary">
                                    Last changed {moment(suspensionNotice.updatedAt).format("DD.MM.YYYY HH:mm")}
                                    {suspensionNotice.updatedBy ? ` by ${suspensionNotice.updatedBy}` : ""}
                                </Text>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
